import { useState, useEffect, useCallback } from 'react';
import { Scale, ScaleFormData, ScaleFilters } from '../types/scale.types';
import * as scaleService from '../services/scaleService';
import { useAuth } from './useAuth';
import { auth } from '../firebase/config';

export const useScales = () => {
  const [scales, setScales] = useState<Scale[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ScaleFilters>({
    searchTerm: '',
    scaleType: 'all',
    isActive: 'all',
  });
  const { user } = useAuth();

  // Load scales with filters
  const loadScales = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { scales: fetchedScales } = await scaleService.getScales({
        searchTerm: filters.searchTerm || undefined,
        scaleType: filters.scaleType !== 'all' ? filters.scaleType : undefined,
        isActive:
          filters.isActive === 'all'
            ? undefined
            : filters.isActive === 'true',
      });
      setScales(fetchedScales);
    } catch (err) {
      setError('Failed to load scales');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadScales();
  }, [loadScales]);

  // Create scale
  const createScale = async (scaleData: Omit<ScaleFormData, 'createdAt' | 'updatedAt'>) => {
    // Get user from localStorage (your app login)
    const storedUser = localStorage.getItem("currentUser");

    if (!storedUser) {
      throw new Error('User not authenticated');
    }

    const appUser = JSON.parse(storedUser);

    setLoading(true);
    setError(null);
    try {
      const scaleId = await scaleService.createScale(scaleData, appUser.id);
      await loadScales();
      return scaleId;
    } catch (err) {
      setError('Failed to create scale');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update scale
  const updateScale = async (scaleId: string, scaleData: Partial<ScaleFormData>) => {
    setLoading(true);
    setError(null);
    try {
      await scaleService.updateScale(scaleId, scaleData);
      await loadScales(); // Reload to get updated list
    } catch (err) {
      setError('Failed to update scale');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Delete scale
  const deleteScale = async (scaleId: string) => {
    setLoading(true);
    setError(null);
    try {
      await scaleService.deleteScale(scaleId);
      await loadScales(); // Reload to get updated list
    } catch (err) {
      setError('Failed to delete scale');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Toggle status
  const toggleStatus = async (scaleId: string, isActive: boolean) => {
    try {
      await scaleService.toggleScaleStatus(scaleId, isActive);
      await loadScales();
    } catch (err) {
      setError('Failed to toggle scale status');
      throw err;
    }
  };

  // Bulk delete
  const bulkDelete = async (scaleIds: string[]) => {
    setLoading(true);
    setError(null);
    try {
      await scaleService.bulkDeleteScales(scaleIds);
      await loadScales();
    } catch (err) {
      setError('Failed to delete scales');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    scales,
    loading,
    error,
    filters,
    setFilters,
    createScale,
    updateScale,
    deleteScale,
    toggleStatus,
    bulkDelete,
    refreshScales: loadScales,
  };
};